import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Bell, Check, Archive, Trash2, X, ThumbsUp, ThumbsDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function NotificationsBell({ audience = "user", testid = "notif-bell" }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(null); // notif being rejected
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [rejectScope, setRejectScope] = useState("content");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { audience, limit: 40 } });
      setItems(data.items || []); setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 45000);
    return () => clearInterval(iv);
  }, [audience]);

  // Same rationale as NotificationsPage: pass validateStatus so Axios never
  // throws on 404 for notification mutations — the console stays clean.
  const AX_OK = { validateStatus: (s) => (s >= 200 && s < 300) || s === 404 };

  // Safe wrappers: 404 on a specific notification means the row is gone
  // server-side (deleted content, cascaded cleanup, or already-removed by
  // another tab). Drop it locally, keep the app usable, no red console noise.
  const safeCall = async (call, notifId) => {
    try {
      const res = await call();
      const status = res && typeof res === "object" ? res.status : 200;
      if (status === 404 && notifId) {
        setItems((arr) => arr.filter((x) => x.notif_id !== notifId));
        setUnread((u) => Math.max(0, u - 1));
      }
      await load();
    } catch (e) {
      const status = e?.response?.status;
      if (status && status !== 401) toast.error(e?.response?.data?.detail || t("generic_error"));
    }
  };

  const markRead = (n) => safeCall(() => api.patch(`/notifications/${n.notif_id}`, { read: true }, AX_OK), n.notif_id);
  const openItem = async (n) => {
    await markRead(n);
    setOpen(false);
    if (n.link) nav(n.link);
  };
  const del = (n) => safeCall(() => api.delete(`/notifications/${n.notif_id}`, AX_OK), n.notif_id);
  const archive = (n) => safeCall(() => api.patch(`/notifications/${n.notif_id}`, { archived: true }, AX_OK), n.notif_id);
  const markAllRead = () => safeCall(() => api.post("/notifications/mark-all-read", null, { params: { audience }, ...AX_OK }), null);

  const isActionable = (n) => {
    if (audience !== "admin") return false;
    const m = n.meta || {};
    if (m.handled) return false;
    return !!(m.report_id || m.alert_id);
  };
  const approve = async (n) => {
    setBusyId(n.notif_id);
    try {
      const { data } = await api.post(`/admin/notifications/${n.notif_id}/approve`, {});
      if (data.ok) toast.success("Approvata"); else toast.error(data.message || "Errore");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusyId(null); }
  };
  const openReject = (n) => { setRejectOpen(n); setRejectReason(""); setRejectNote(""); setRejectScope("content"); };
  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error("Motivazione obbligatoria"); return; }
    const n = rejectOpen;
    setBusyId(n.notif_id);
    try {
      const { data } = await api.post(`/admin/notifications/${n.notif_id}/reject`, {
        reason: rejectReason.trim(), note: rejectNote.trim() || null, scope: rejectScope,
      });
      if (data.ok) toast.success("Rifiuto registrato"); else toast.error(data.message || "Errore");
      setRejectOpen(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="relative">
      <button data-testid={testid} onClick={() => setOpen((v) => !v)} className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-slate-100 text-slate-700" aria-label={t("notifications")}>
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid={`${testid}-count`} className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[999998]" onClick={() => setOpen(false)} data-testid={`${testid}-backdrop`} />
          <div className="fixed top-14 right-2 sm:right-4 left-2 sm:left-auto sm:w-[380px] max-h-[75vh] rounded-2xl bg-white border border-slate-200 shadow-2xl z-[999999] overflow-hidden flex flex-col" data-testid={`${testid}-panel`}>
          <div className="p-3 flex items-center justify-between border-b border-slate-100">
            <div className="font-heading font-semibold text-slate-900 text-sm">{t("notifications")}</div>
            <div className="flex items-center gap-1">
              <button data-testid={`${testid}-all-read`} onClick={markAllRead} className="text-[11px] px-2 py-1 rounded-full hover:bg-emerald-50 text-emerald-700"><Check size={12} className="inline mr-1" />{t("notif_all_read")}</button>
              <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-full hover:bg-slate-100 text-slate-500 flex items-center justify-center"><X size={14} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">{t("notif_empty")}</div>
            ) : items.map((n) => (
              <div key={n.notif_id} data-testid={`notif-${n.notif_id}`} className={`px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${!n.read ? "bg-emerald-50/40" : ""}`}>
                <div className="flex items-start gap-2">
                  <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${!n.read ? "bg-emerald-500" : "bg-transparent"}`} />
                  <button onClick={() => openItem(n)} className="flex-1 text-left min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t(`notif_type_${n.type}`) || n.type}</div>
                    <div className="text-sm text-slate-900 font-semibold truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2 break-words">{n.body}</div>}
                    <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </button>
                  <div className="flex flex-col gap-1">
                    {!n.read && <button data-testid={`notif-read-${n.notif_id}`} onClick={() => markRead(n)} className="w-6 h-6 rounded-full hover:bg-emerald-100 text-emerald-700 flex items-center justify-center" title={t("notif_mark_read")}><Check size={12} /></button>}
                    <button data-testid={`notif-archive-${n.notif_id}`} onClick={() => archive(n)} className="w-6 h-6 rounded-full hover:bg-slate-100 text-slate-500 flex items-center justify-center" title={t("notif_archive")}><Archive size={12} /></button>
                    <button data-testid={`notif-delete-${n.notif_id}`} onClick={() => del(n)} className="w-6 h-6 rounded-full hover:bg-red-50 text-red-600 flex items-center justify-center" title={t("notif_delete")}><Trash2 size={12} /></button>
                  </div>
                </div>
                {isActionable(n) && (
                  <div className="mt-2 ml-4 flex items-center gap-2 flex-wrap">
                    <button
                      data-testid={`notif-approve-${n.notif_id}`}
                      onClick={() => approve(n)}
                      disabled={busyId === n.notif_id}
                      className="h-7 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <ThumbsUp size={11} /> Approva
                    </button>
                    <button
                      data-testid={`notif-reject-${n.notif_id}`}
                      onClick={() => openReject(n)}
                      disabled={busyId === n.notif_id}
                      className="h-7 px-3 rounded-full border border-red-300 hover:bg-red-50 text-red-700 text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <ThumbsDown size={11} /> Rifiuta
                    </button>
                    {n.link && (
                      <button
                        data-testid={`notif-open-${n.notif_id}`}
                        onClick={() => openItem(n)}
                        className="h-7 px-3 rounded-full border border-slate-300 hover:bg-slate-100 text-slate-700 text-[11px] font-semibold inline-flex items-center gap-1"
                      >
                        <ExternalLink size={11} /> Apri dettaglio
                      </button>
                    )}
                  </div>
                )}
                {n.meta?.handled && (
                  <div className="mt-1 ml-4 text-[10px] text-slate-500 italic" data-testid={`notif-handled-${n.notif_id}`}>
                    Contenuto già gestito ({n.meta.decision})
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-2 border-t border-slate-100">
            <button
              data-testid={`${testid}-view-all`}
              onClick={() => { setOpen(false); nav("/notifications"); }}
              className="w-full text-center py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 rounded-lg"
            >{t("notif_view_all") || "Visualizza tutte le notifiche"}</button>
          </div>
        </div>
        </>,
        document.body
      )}
      {rejectOpen && createPortal(
        <div className="fixed inset-0 z-[1000000] bg-black/40 flex items-center justify-center p-4" data-testid="notif-reject-dialog">
          <div className="bg-white rounded-2xl w-full max-w-sm p-4 shadow-2xl">
            <div className="font-heading font-bold text-slate-900 text-base mb-2">Motivazione rifiuto</div>
            <div className="text-xs text-slate-500 mb-3">La motivazione è obbligatoria. La nota è facoltativa e non sarà mostrata all'autore.</div>
            <div className="mb-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ambito</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <button type="button" data-testid="reject-scope-photo" onClick={() => setRejectScope("photo_only")} className={`h-9 rounded-full text-xs font-semibold border ${rejectScope === "photo_only" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>Solo foto</button>
                <button type="button" data-testid="reject-scope-content" onClick={() => setRejectScope("content")} className={`h-9 rounded-full text-xs font-semibold border ${rejectScope === "content" ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-slate-200"}`}>Intera segnalazione</button>
              </div>
            </div>
            <div className="mb-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Motivazione *</label>
              <textarea
                data-testid="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-xl border border-slate-200 p-2 text-sm"
                placeholder="Es. Volto riconoscibile non mascherato correttamente"
              />
            </div>
            <div className="mb-3">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Nota (interna, facoltativa)</label>
              <textarea
                data-testid="reject-note"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-xl border border-slate-200 p-2 text-sm"
                placeholder="Nota per il log Admin"
              />
            </div>
            <div className="flex gap-2">
              <button data-testid="reject-cancel" onClick={() => setRejectOpen(null)} className="flex-1 h-9 rounded-full border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100">Annulla</button>
              <button data-testid="reject-submit" onClick={submitReject} disabled={!rejectReason.trim()} className="flex-1 h-9 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-50">Conferma rifiuto</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
